import React, { useEffect, useMemo, useState } from 'react';
import { db, appId } from '../utils/firebase';
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc, deleteField, doc, serverTimestamp } from 'firebase/firestore';
import { Icons } from '../components/Icons';
import { uploadMedia } from '../utils/uploadMedia';
import { safeHtml } from '../utils/safeHtml';
import { CREW_ROLES, CARD_KINDS, kindsForRole, textToHtml, htmlToText, STATUS_LABEL } from '../utils/crew';
import CommsView from './CommsView';

const field = 'w-full bg-[#111] border border-gray-800 rounded p-3 text-white text-sm outline-none focus:border-green-600';
const label = 'block text-[11px] font-bold uppercase tracking-widest text-gray-400 mb-1';
const help = 'text-[11px] text-gray-600 mt-1 leading-relaxed';
const dataPath = (...p) => ['artifacts', appId, 'public', 'data', ...p];

// ---------- the form for one card ----------
// Every card has a FRONT (what players see) and a BACK (what only the team sees). The labels say which is which.
const CrewCardForm = ({ kind, existing, handle, uid, onClose, showToast }) => {
    const k = CARD_KINDS[kind];
    const [title, setTitle] = useState(existing ? (existing.title || existing.name || '') : '');
    const [hint, setHint] = useState(existing ? (existing.hint || '') : '');
    const [desc, setDesc] = useState(existing ? htmlToText(existing.desc) : '');
    const [suggestedSite, setSuggestedSite] = useState(existing ? (existing.suggestedSite || '') : '');
    const [nextLead, setNextLead] = useState(existing ? (existing.nextLead || '') : '');
    const [creatorNotes, setCreatorNotes] = useState(existing ? (existing.creatorNotes || '') : '');
    const [collab, setCollab] = useState(existing ? !!existing.collab : false);
    const [collaborators, setCollaborators] = useState(existing ? (existing.collaborators || '') : '');
    const [imageUrl, setImageUrl] = useState(existing ? (existing.imageUrl || '') : '');
    const [audioUrl, setAudioUrl] = useState(existing ? (existing.audioUrl || '') : '');
    const [uploading, setUploading] = useState('');
    const [saving, setSaving] = useState(false);

    const needsClue = ['DOOR', 'OBJECT', 'SOUND'].includes(kind);
    const needsSite = ['DOOR', 'OBJECT', 'SOUND', 'SCOUT'].includes(kind);
    const showNext = kind !== 'SCOUT';
    const wantsPhoto = kind !== 'LORE';

    const upload = async (e, which) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        setUploading(which);
        try {
            const url = await uploadMedia(file);
            if (which === 'image') setImageUrl(url); else setAudioUrl(url);
        } catch (err) { showToast(`Upload failed: ${err.message || 'try again.'}`, 'error'); }
        setUploading('');
        e.target.value = null;
    };

    const save = async (e) => {
        e.preventDefault();
        if (!title.trim()) return showToast('Give it a title.', 'error');
        if (kind === 'SOUND' && !audioUrl) return showToast('Upload the audio first.', 'error');
        setSaving(true);
        // when editing, a field the person cleared must really be removed, so it is sent as deleteField()
        const opt = (key, value) => (value ? { [key]: value } : (existing ? { [key]: deleteField() } : {}));
        const payload = {
            kind, title: title.trim(), desc: textToHtml(desc), status: 'DRAFT',
            createdBy: uid, createdByHandle: handle,
            ...(needsClue ? opt('hint', hint.trim()) : {}),
            ...(needsSite ? opt('suggestedSite', suggestedSite.trim()) : {}),
            ...(showNext ? opt('nextLead', nextLead.trim()) : {}),
            ...opt('creatorNotes', creatorNotes.trim()),
            collab: !!collab,
            ...opt('collaborators', collab ? collaborators.trim() : ''),
            ...opt('imageUrl', imageUrl),
            ...opt('audioUrl', audioUrl),
            ...(kind === 'DOOR' ? { designation: 'DOOR' } : {}),
            ...(kind === 'LORE' ? { tag: 'LORE' } : {}),
        };
        try {
            const coll = collection(db, ...dataPath(k.col));
            if (existing) await updateDoc(doc(coll, existing.id), payload);
            else await addDoc(coll, { ...payload, createdAt: serverTimestamp() });
            showToast('Saved. The team will review it.', 'success');
            onClose();
        } catch (err) {
            console.warn(err);
            showToast('Could not save. If the team has already started editing this card, it is locked.', 'error');
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/90 z-[200] overflow-y-auto p-4" data-testid="crew-form">
            <form onSubmit={save} className="bg-[#0a0a0a] border border-gray-800 rounded max-w-xl mx-auto my-6 p-6 md:p-8 space-y-5">
                <div className="flex justify-between items-center border-b border-gray-900 pb-3">
                    <h3 className="font-black uppercase tracking-widest text-lg">{existing ? 'Edit' : 'New'} {k.label.toLowerCase()}</h3>
                    <button type="button" onClick={onClose} className="text-gray-500 hover:text-white text-xs font-bold uppercase">Close</button>
                </div>

                <div>
                    <label className={label}>Title</label>
                    <input value={title} onChange={e => setTitle(e.target.value)} maxLength={120} className={field} placeholder={kind === 'DOOR' ? 'For example: The green door on Airport Way' : 'A short name'} required />
                </div>

                {wantsPhoto && (
                    <div>
                        <label className={label}>{kind === 'DOOR' ? 'Photo (straight on, whole door in frame)' : 'Photo'}</label>
                        {imageUrl && <img src={imageUrl} alt="" className="max-h-48 rounded border border-gray-800 mb-2" />}
                        <label className="inline-block px-4 py-2 border border-dashed border-gray-600 rounded text-xs text-gray-300 cursor-pointer hover:text-white">
                            {uploading === 'image' ? 'Uploading...' : (imageUrl ? 'Change photo' : '+ Add a photo')}
                            <input type="file" accept="image/*" onChange={e => upload(e, 'image')} className="hidden" disabled={!!uploading} />
                        </label>
                    </div>
                )}

                {kind === 'SOUND' && (
                    <div>
                        <label className={label}>Audio file</label>
                        {audioUrl && <audio controls src={audioUrl} className="w-full mb-2" />}
                        <label className="inline-block px-4 py-2 border border-dashed border-gray-600 rounded text-xs text-gray-300 cursor-pointer hover:text-white">
                            {uploading === 'audio' ? 'Uploading...' : (audioUrl ? 'Change audio' : '+ Add audio (mp3, wav, m4a)')}
                            <input type="file" accept=".mp3,.wav,.m4a,.ogg,audio/*" onChange={e => upload(e, 'audio')} className="hidden" disabled={!!uploading} />
                        </label>
                    </div>
                )}

                {needsClue && (
                    <div className="border-l-2 border-yellow-600 pl-4">
                        <label className={label}>Clue <span className="text-yellow-500">(players see this BEFORE they find it)</span></label>
                        <textarea value={hint} onChange={e => setHint(e.target.value)} maxLength={500} className={`${field} h-20`} placeholder="One or two sentences that point to the place without naming it." />
                    </div>
                )}

                <div className={kind === 'LORE' ? '' : 'border-l-2 border-yellow-600 pl-4'}>
                    <label className={label}>
                        {kind === 'LORE' ? 'The story' : kind === 'SCOUT' ? 'What you found' : <>What players see or hear <span className="text-yellow-500">(AFTER they find it)</span></>}
                    </label>
                    <textarea value={desc} onChange={e => setDesc(e.target.value)} className={`${field} ${kind === 'LORE' ? 'h-72' : 'h-32'}`}
                        placeholder={kind === 'DOOR' ? 'Describe it plainly: colors, materials, any words or marks. The team also uses this to recognise it.' : 'Write it the way players should read it.'} />
                    <p className={help}>{kind === 'LORE' ? 'Leave a blank line between paragraphs.' : 'Plain text is fine. Blank line = new paragraph.'}</p>
                </div>

                <div className="border-l-2 border-green-800 pl-4 space-y-5">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-green-500">Team only (players never see these)</div>
                    {needsSite && (
                        <div>
                            <label className={label}>Suggested site</label>
                            <input value={suggestedSite} onChange={e => setSuggestedSite(e.target.value)} maxLength={300} className={field} placeholder="Neighborhood, address or place name" />
                            <p className={help}>Only a suggestion. The team places the real spot on the map.</p>
                        </div>
                    )}
                    {showNext && (
                        <div>
                            <label className={label}>What does this lead to next?</label>
                            <textarea value={nextLead} onChange={e => setNextLead(e.target.value)} maxLength={500} className={`${field} h-16`} placeholder="Another door, a person, a song, a place, a secret..." />
                        </div>
                    )}
                    <div>
                        <label className="flex items-center gap-2 text-sm text-gray-200 cursor-pointer">
                            <input type="checkbox" checked={collab} onChange={e => setCollab(e.target.checked)} className="accent-green-500" />
                            This is a collaboration
                        </label>
                        {collab && <input value={collaborators} onChange={e => setCollaborators(e.target.value)} maxLength={300} className={`${field} mt-2`} placeholder="Who else made it or is helping?" />}
                    </div>
                    <div>
                        <label className={label}>Notes for the team</label>
                        <textarea value={creatorNotes} onChange={e => setCreatorNotes(e.target.value)} maxLength={1000} className={`${field} h-16`} placeholder="Anything we should know" />
                    </div>
                </div>

                <button type="submit" disabled={saving || !!uploading} className="w-full bg-green-700 hover:bg-green-600 text-white py-3 font-bold uppercase tracking-widest text-xs rounded disabled:opacity-50">{saving ? 'Saving...' : 'Save draft for review'}</button>
            </form>
        </div>
    );
};

// ---------- the portal ----------
const CrewPortal = ({ handle, role, uid, onExit, showToast: showToastProp }) => {
    const [tab, setTab] = useState('work');
    const [artifacts, setArtifacts] = useState([]);
    const [ideas, setIdeas] = useState([]);
    const [shared, setShared] = useState({ artifacts: [], ideas: [], characters: [], journals: [] });
    const [sharedComms, setSharedComms] = useState([]);
    const [form, setForm] = useState(null); // { kind, existing }
    const [toast, setToast] = useState(null);

    const showToast = showToastProp || ((message, type = 'success') => { setToast({ message, type }); setTimeout(() => setToast(null), 3500); });
    const kinds = kindsForRole(role);
    const roleInfo = CREW_ROLES[role] || { label: 'Crew', blurb: '' };

    useEffect(() => {
        const subs = [];
        const watch = (col, q, set) => subs.push(onSnapshot(q, snap => set(snap.docs.map(d => ({ id: d.id, ...d.data() }))), err => console.warn(`crew: ${col}`, err.code)));
        watch('my artifacts', query(collection(db, ...dataPath('artifacts')), where('createdBy', '==', uid)), setArtifacts);
        watch('my ideas', query(collection(db, ...dataPath('ideas')), where('createdBy', '==', uid)), setIdeas);
        ['artifacts', 'ideas', 'characters', 'journals'].forEach(col =>
            watch(`shared ${col}`, query(collection(db, ...dataPath(col)), where('shareWithCrew', '==', true)), list => setShared(prev => ({ ...prev, [col]: list }))));
        watch('shared comms', query(collection(db, ...dataPath('comms')), where('shared', '==', true)), setSharedComms);
        return () => subs.forEach(u => u());
    }, [uid]);

    const mine = useMemo(() => [
        ...artifacts.map(a => ({ ...a, _col: 'artifacts' })),
        ...ideas.map(a => ({ ...a, _col: 'ideas' })),
    ].sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0)), [artifacts, ideas]);

    const sharedList = useMemo(() => Object.entries(shared).flatMap(([col, list]) => list.map(i => ({ ...i, _col: col }))), [shared]);

    const remove = async (item) => {
        if (!window.confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
        try { await deleteDoc(doc(db, ...dataPath(item._col, item.id))); showToast('Deleted.', 'success'); }
        catch (e) { showToast('Could not delete that.', 'error'); }
    };

    const tabBtn = (id, text) => (
        <button onClick={() => setTab(id)} className={`px-5 py-3 text-xs font-bold uppercase tracking-widest border-b-2 whitespace-nowrap ${tab === id ? 'border-green-500 text-white' : 'border-transparent text-gray-500 hover:text-gray-300'}`}>{text}</button>
    );

    return (
        <div className="h-full flex flex-col bg-[#050505] text-white" data-testid="crew-portal">
            <header className="flex items-center justify-between px-5 py-4 border-b border-gray-900 shrink-0">
                <div className="flex items-center gap-3">
                    <div className="h-8 w-8 text-gray-500"><Icons.TriangleLogo className="w-full h-full" /></div>
                    <div>
                        <div className="font-black tracking-[0.2em] uppercase text-sm">Crew</div>
                        <div className="text-[11px] text-gray-500">{handle} &middot; {roleInfo.label}</div>
                    </div>
                </div>
                <button onClick={onExit} className="text-xs font-bold uppercase tracking-widest text-gray-500 hover:text-white border border-gray-800 px-3 py-2 rounded">Sign out</button>
            </header>
            <nav className="flex border-b border-gray-900 overflow-x-auto shrink-0 px-2">
                {tabBtn('work', 'My work')}
                {tabBtn('chat', 'Crew chat')}
                {tabBtn('shared', `Shared with you${sharedList.length ? ` (${sharedList.length})` : ''}`)}
                {tabBtn('guide', 'How it works')}
            </nav>

            <main className="flex-1 overflow-y-auto relative">
                {tab === 'work' && (
                    <div className="max-w-3xl mx-auto p-5 md:p-8">
                        <p className="text-sm text-gray-400 mb-5">{roleInfo.blurb} Add a card, and the team will review it. Nothing goes live until we approve it.</p>
                        <div className="flex flex-wrap gap-3 mb-8">
                            {kinds.map(kd => (
                                <button key={kd} onClick={() => setForm({ kind: kd })} className="px-5 py-3 bg-green-700 hover:bg-green-600 text-white text-xs font-bold uppercase tracking-widest rounded">{CARD_KINDS[kd].add}</button>
                            ))}
                        </div>
                        {mine.length === 0 ? (
                            <div className="border border-dashed border-gray-800 rounded p-10 text-center text-gray-600 text-sm">Nothing yet. Add your first card above.</div>
                        ) : (
                            <div className="space-y-3">
                                {mine.map(item => (
                                    <div key={item._col + item.id} className="bg-[#0a0a0a] border border-gray-900 rounded p-4 flex gap-4">
                                        {item.imageUrl ? <img src={item.imageUrl} alt="" className="w-20 h-20 object-cover rounded border border-gray-800 shrink-0" /> : <div className="w-20 h-20 rounded border border-gray-900 bg-black shrink-0 flex items-center justify-center text-gray-700"><Icons.Box size={22} /></div>}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <h4 className="font-bold truncate">{item.title}</h4>
                                                <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border border-gray-700 text-gray-400">{(CARD_KINDS[item.kind] || {}).label || 'Card'}</span>
                                                <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${item.status === 'VERIFIED' ? 'border-green-800 text-green-400' : 'border-yellow-800 text-yellow-500'}`}>{STATUS_LABEL(item.status)}</span>
                                                {item.collab && <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border border-blue-900 text-blue-400">Collab</span>}
                                            </div>
                                            {item.suggestedSite && <p className="text-xs text-gray-500 mt-1 truncate">Suggested site: {item.suggestedSite}</p>}
                                            {(item.status === 'DRAFT' || !item.status) && (
                                                <div className="flex gap-4 mt-3 text-xs">
                                                    <button onClick={() => setForm({ kind: item.kind, existing: item })} className="text-gray-300 hover:text-white underline underline-offset-4">Edit</button>
                                                    <button onClick={() => remove(item)} className="text-red-500 hover:text-red-400 underline underline-offset-4">Delete</button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {tab === 'chat' && (
                    <div className="h-full flex flex-col">
                        <CommsView data={{ comms: sharedComms }} userData={{ handle, clearance: 'CREW' }} isAdmin={false} showToast={showToast} localComms={[]} setLocalComms={() => {}} crewMode />
                    </div>
                )}

                {tab === 'shared' && (
                    <div className="max-w-3xl mx-auto p-5 md:p-8 space-y-4">
                        <p className="text-sm text-gray-400">Reference material the team chose to share with the crew. Read only.</p>
                        {sharedList.length === 0 && <div className="border border-dashed border-gray-800 rounded p-10 text-center text-gray-600 text-sm">Nothing shared yet.</div>}
                        {sharedList.map(item => (
                            <div key={item._col + item.id} className="bg-[#0a0a0a] border border-gray-900 rounded p-5">
                                <h4 className="font-bold mb-2">{item.title || item.name}</h4>
                                {item.imageUrl && <img src={item.imageUrl} alt="" className="max-h-48 rounded border border-gray-800 mb-3" />}
                                <div className="text-sm text-gray-300 leading-relaxed [&_p]:mb-3" dangerouslySetInnerHTML={{ __html: safeHtml(item.desc || item.lore || item.content || item.text || '') }} />
                            </div>
                        ))}
                    </div>
                )}

                {tab === 'guide' && (
                    <div className="max-w-2xl mx-auto p-5 md:p-8 space-y-5 text-sm text-gray-300 leading-relaxed">
                        <h3 className="font-black uppercase tracking-widest text-lg text-white">How it works</h3>
                        <p>Every card has two sides.</p>
                        <p><span className="text-yellow-500 font-bold">What players see.</span> The <b>clue</b> they get before they find it, and the <b>text, picture or sound</b> they get once they do. Write these for players.</p>
                        <p><span className="text-green-500 font-bold">What only the team sees.</span> Your suggested site, what the card leads to next, who you made it with, and your notes. Players never see these.</p>
                        <ol className="list-decimal ml-5 space-y-2">
                            <li>Add a card. It is saved as a draft that only you and the team can see.</li>
                            <li>The team reviews it, picks the real spot on the map, and finishes it.</li>
                            <li>When it is approved it can go into the game. You will see it marked Approved.</li>
                        </ol>
                        <p>You can edit or delete a card until the team approves it. Use Crew chat to talk with everyone.</p>
                    </div>
                )}
            </main>

            {form && <CrewCardForm kind={form.kind} existing={form.existing} handle={handle} uid={uid} onClose={() => setForm(null)} showToast={showToast} />}
            {toast && (
                <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[300] px-5 py-3 rounded border text-xs font-bold ${toast.type === 'error' ? 'bg-black border-red-700 text-red-400' : 'bg-black border-green-700 text-green-400'}`}>{toast.message}</div>
            )}
        </div>
    );
};

export default CrewPortal;
